/**
 * @file auth-user.token.ts
 *
 * Tiny injection token + interface that decouples `DashboardPageEngine`
 * (and any other engine-like base class) from the concrete `AuthService`.
 *
 * Why this exists
 * ---------------
 * Without this indirection, the static import graph contains a cycle:
 *
 *   component-registry.ts
 *     └── monthly-report-2.component.ts (eagerly imported)
 *           └── dashboard-page-engine.ts
 *                 └── auth.service.ts
 *                       └── component-registry.ts   ← cycle back
 *
 * When the eager bundle evaluates that cycle, `DashboardPageEngine` is
 * not yet bound at the moment `MonthlyReport2Component extends
 * DashboardPageEngine` runs, producing:
 *
 *   "Class extends value undefined is not a constructor or null"
 *
 * Routing this single dependency through a plain `InjectionToken` (this
 * file has zero app-level imports) removes the back-edge from the static
 * graph without changing runtime behavior — the engine still ends up
 * with the live `AuthService` instance because we register it via
 * `useExisting: AuthService` in `app.config.ts`.
 */

import { InjectionToken } from '@angular/core';

/**
 * Minimal shape the dashboard engine needs from the auth layer. Kept
 * loose on purpose so this token never has to know about the full
 * `User` interface exported by `auth.service.ts`.
 */
export interface CurrentUserAccessor {
  currentUser(): { role_id?: unknown; roleId?: unknown; [key: string]: unknown } | null;
}

export const CURRENT_USER_ACCESSOR = new InjectionToken<CurrentUserAccessor>(
  'CURRENT_USER_ACCESSOR',
);
